import { formatDateBR } from '@/lib/format';
import type { CardFieldMap, ChipTone, DetailFieldMap, UserRef } from '@/modules/kanban';

// Mapeia card → slots visuais. Específico do dominio fundiário (GEO/CCIR/Titulação).
export const fundiarioCardFieldMap: CardFieldMap = (card) => {
  const cliente = String(card.cliente_nome ?? '—');
  const matricula = card.imovel_matricula ? `Matrícula ${String(card.imovel_matricula)}` : null;
  const cartorio = card.cartorio ? String(card.cartorio) : null;
  const protocolo = card.numero_protocolo ? String(card.numero_protocolo) : null;
  const statusRaw = card.status ? String(card.status) : null;
  const statusVal = statusRaw ? STATUS_LABEL[statusRaw] ?? statusRaw.replace(/_/g, ' ') : null;

  return {
    title: cliente,
    subtitle: matricula ?? (card.imovel_denominacao ? String(card.imovel_denominacao) : null),
    chips: [
      { label: '', value: statusVal, tone: statusTone(statusRaw) },
      { label: 'Cartório', value: cartorio, tone: 'neutral' },
      { label: 'Protocolo', value: protocolo, tone: 'info' },
    ],
    meta: card.municipio ? String(card.municipio) : null,
  };
};

function parseResponsaveis(raw: unknown): UserRef[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r): UserRef | null => {
      if (typeof r === 'string') return null;
      if (r && typeof r === 'object' && 'id' in r) {
        const obj = r as Record<string, unknown>;
        return {
          id: Number(obj.id),
          name: typeof obj.name === 'string' ? obj.name : null,
          email: typeof obj.email === 'string' ? obj.email : null,
        };
      }
      return null;
    })
    .filter((u): u is UserRef => u != null && Number.isFinite(u.id));
}

const STATUS_LABEL: Record<string, string> = {
  em_andamento: 'em andamento',
  em_analise: 'em análise',
  protocolado: 'protocolado',
  concluido: 'concluído',
  cancelado: 'cancelado',
  suspenso: 'suspenso',
  aguardando_incra: 'aguardando INCRA',
  aceito: 'aceito',
  rejeitado: 'rejeitado',
};

function statusTone(s: string | null | undefined): ChipTone {
  switch (s) {
    case 'concluido':
    case 'aceito':
      return 'ok';
    case 'em_analise':
    case 'protocolado':
    case 'aguardando_incra':
      return 'info';
    case 'em_andamento':
      return 'accent';
    case 'cancelado':
    case 'suspenso':
    case 'rejeitado':
      return 'danger';
    default:
      return 'neutral';
  }
}

// ── Selects (alinhados com o legado) ──
const TIPO_PROPRIEDADE = [
  { value: 'proprio', label: 'Próprio' },
  { value: 'arrendado', label: 'Arrendado' },
  { value: 'posse', label: 'Posse' },
];
const VINCULO_DOMINIAL = [
  { value: 'matriculado', label: 'Matriculado' },
  { value: 'posse', label: 'Posse' },
  { value: 'contrato', label: 'Contrato' },
  { value: 'outro', label: 'Outro' },
];
const VIAVEL = [
  { value: 'sim', label: 'Sim' },
  { value: 'nao', label: 'Não' },
  { value: 'pendente', label: 'Pendente' },
];
const CONDICOES_PAGAMENTO_TRIAGEM = [
  { value: 'avista', label: 'Opção 1 — Pagamento à vista' },
  { value: 'entrada_50_restante_10_dias', label: 'Opção 2 — Entrada de 50% e restante após 10 dias' },
  { value: 'entrada_50_restante_30_dias', label: 'Opção 3 — Entrada de 50% e restante após 30 dias' },
];
const SERVICOS_PROPOSTA_FUNDIARIA = [
  'Georreferenciamento para certificação no INCRA',
  'Emissão de mapa e memorial descritivo',
  'Unificação e retificação administrativa dos dados descritivos das matrículas',
  'Desmembramento de matrícula',
  'Estudo, relatório e acompanhamento para divisão de lotes ou confrontação de divisas',
  'Retificação administrativa dos dados descritivos das matrículas',
  'Consolidação dos dados do imóvel rural para atualização e regularização do ITR',
  'Emissão ou atualização do CCIR',
  'Alteração de titularidade do georreferenciamento para certificação no INCRA',
].map((label) => ({ value: label, label }));
const OUTRO_IMOVEL_VALUE = '__outro__';
const REGIME_CASAMENTO = [
  { value: 'comunhao_parcial', label: 'Comunhão parcial' },
  { value: 'comunhao_universal', label: 'Comunhão universal' },
  { value: 'separacao_total', label: 'Separação total' },
  { value: 'participacao_final', label: 'Participação final' },
];
const FORMA_ENTREGA = [
  { value: 'pessoal', label: 'Pessoal' },
  { value: 'cartorio', label: 'Cartório' },
  { value: 'digital', label: 'Digital' },
  { value: 'correios', label: 'Correios' },
];
const CCIR_STATUS_INCRA = [
  { value: 'em_andamento', label: 'Pendente' },
  { value: 'aguardando_incra', label: 'Aguardando INCRA' },
  { value: 'aceito', label: 'Aceito' },
  { value: 'rejeitado', label: 'Rejeitado' },
  { value: 'em_analise', label: 'Em análise' },
];
const TIT_DOC_ORIGINARIO = [
  { value: 'ccu', label: 'CCU — Contrato de Concessão de Uso' },
  { value: 'compra_venda', label: 'Contrato compra/venda' },
  { value: 'doacao', label: 'Doação' },
  { value: 'posse', label: 'Posse mansa e pacífica' },
  { value: 'outro', label: 'Outro' },
];
const TIT_TIPO_TITULO = [
  { value: 'cro', label: 'CRO — Concessão Real de Uso' },
  { value: 'cdru', label: 'CDRU — Concessão Direito Real de Uso' },
  { value: 'escritura', label: 'Escritura pública' },
  { value: 'titulo_dominio', label: 'Título de domínio pleno' },
];
const TIT_RESULT_VIST = [
  { value: 'aprovado', label: 'Aprovado' },
  { value: 'reprovado', label: 'Reprovado' },
  { value: 'pendencias', label: 'Com pendências' },
];
const TIT_CADEIA_DOM = [
  { value: 'completa', label: 'Completa' },
  { value: 'lacunas', label: 'Com lacunas' },
  { value: 'desde_2008', label: 'Desde 2008' },
  { value: 'incompleta', label: 'Incompleta' },
];

function labelFromOptions(
  v: unknown,
  opts: Array<{ value: string; label: string }>,
): string | null {
  if (v == null || v === '') return null;
  const found = opts.find((o) => o.value === String(v));
  return found ? found.label : String(v);
}

interface PhaseField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'boolean' | 'select' | 'multiselect' | 'emitir_proposta_fundiaria';
  options?: Array<{ value: string; label: string }>;
  showIf?: (c: Record<string, unknown>) => boolean;
}

interface PhaseDef {
  name: string;
  fields: PhaseField[];
}

interface PipeSpec {
  colStart: number; // kanban_coluna do índice 1
  /** Ordem explícita para pipes cujos IDs não são contíguos. */
  columnIds?: number[];
  phases: PhaseDef[];
}

const isPosse = (c: Record<string, unknown>) =>
  c.tipo_vinculo_dominial === 'posse' || c.tipo_vinculo_dominial === 'contrato';

// ── Triagem Fundiário (card pai) ──
const PIPE_TRIAGEM_GEO: PipeSpec = {
  colStart: 30,
  columnIds: [30, 31, 34, 35, 32, 33],
  phases: [
    {
      name: 'Entrada',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'area_estimada_ha', label: 'Área estimada (ha)', type: 'number' },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
        { key: 'observacoes', label: 'Contexto da demanda', type: 'textarea' },
      ],
    },
    {
      name: 'Análise',
      fields: [
        { key: 'tipo_vinculo_dominial', label: 'Vínculo dominial', type: 'select', options: VINCULO_DOMINIAL },
        { key: 'descricao_vinculo', label: 'Descrição do vínculo', type: 'text', showIf: isPosse },
        { key: 'viavel', label: 'Viável para GEO?', type: 'select', options: VIAVEL },
        { key: 'observacoes_entrada', label: 'Conclusão da análise', type: 'textarea' },
      ],
    },
    {
      name: 'Emissão da Proposta',
      fields: [
        { key: 'servicos_orcados', label: 'Serviços incluídos na proposta', type: 'multiselect', options: SERVICOS_PROPOSTA_FUNDIARIA },
        { key: 'imoveis_contrato', label: 'Imóveis incluídos na proposta/contrato', type: 'multiselect' },
        {
          key: 'imoveis_contrato_outros',
          label: 'Identificação do outro imóvel',
          type: 'textarea',
          showIf: (c) => selectedStrings(c.imoveis_contrato).includes(OUTRO_IMOVEL_VALUE),
        },
        { key: 'valor_total', label: 'Valor total da proposta (R$)', type: 'number' },
        { key: 'plano_pagamento', label: 'Condição de pagamento', type: 'select', options: CONDICOES_PAGAMENTO_TRIAGEM },
        { key: 'data_prazo_entrega', label: 'Prazo previsto de execução', type: 'date' },
        { key: '_emitir_proposta', label: 'Proposta para o cliente', type: 'emitir_proposta_fundiaria' },
      ],
    },
    {
      name: 'Emissão do Contrato',
      fields: [
        { key: 'tipo_contrato', label: 'Tipo / referência do contrato', type: 'text' },
        { key: 'data_contrato', label: 'Data do contrato', type: 'date' },
        { key: 'contratada_responsavel', label: 'Responsável pela emissão', type: 'text' },
        { key: 'assinatura_eletronica', label: 'Enviado para assinatura eletrônica', type: 'boolean' },
        { key: 'contrato_assinado', label: 'Contrato assinado', type: 'boolean' },
      ],
    },
    {
      name: 'Definição',
      fields: [
        { key: 'cartorio', label: 'Cartório', type: 'text' },
        { key: 'responsavel_tecnico', label: 'Responsável técnico', type: 'text' },
        { key: 'data_previsao_conclusao', label: 'Previsão de conclusão', type: 'date' },
        { key: 'pendencias', label: 'Pendências e próximo passo', type: 'textarea' },
      ],
    },
    { name: 'Encaminhado', fields: [] },
  ],
};

// ── Pipe GEO (georreferenciamento) — colunas 1-7 ──
const PIPE_GEO: PipeSpec = {
  colStart: 1,
  phases: [
    {
      name: 'Entrada',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'area_estimada_ha', label: 'Área estimada (ha)', type: 'number' },
        { key: 'tipo_propriedade', label: 'Tipo de propriedade', type: 'select', options: TIPO_PROPRIEDADE },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
        { key: 'tipo_vinculo_dominial', label: 'Vínculo dominial', type: 'select', options: VINCULO_DOMINIAL },
        { key: 'descricao_vinculo', label: 'Descrição do vínculo', type: 'text', showIf: (c) => c.tipo_vinculo_dominial === 'posse' },
        { key: 'viavel', label: 'Viável?', type: 'select', options: VIAVEL },
        { key: 'observacoes_entrada', label: 'Observações de entrada', type: 'textarea' },
        { key: 'cadeia_nominal_desde_2008', label: 'Cadeia nominal desde 2008', type: 'boolean', showIf: (c) => c.tipo_vinculo_dominial === 'posse' },
      ],
    },
    {
      name: 'Documentação',
      fields: [
        { key: 'doc_matricula', label: 'Certidão de inteiro teor / matrícula atualizada', type: 'boolean' },
        { key: 'doc_ccir', label: 'CCIR quitado', type: 'boolean' },
        { key: 'doc_itr', label: 'ITR quitado (últimos 5 anos)', type: 'boolean' },
        { key: 'doc_car', label: 'CAR (cadastro ambiental rural)', type: 'boolean' },
        { key: 'doc_rg_cpf', label: 'RG e CPF do proprietário (e cônjuge)', type: 'boolean' },
        { key: 'doc_certidao_casamento', label: 'Certidão de casamento', type: 'boolean' },
        { key: 'doc_procuracao', label: 'Procuração (se houver)', type: 'boolean' },
        { key: 'regime_casamento', label: 'Regime de casamento', type: 'select', options: REGIME_CASAMENTO },
        { key: 'pendencias_documentacao', label: 'Pendências da documentação', type: 'textarea' },
        { key: 'data_documentacao_completa', label: 'Data documentação completa', type: 'date' },
      ],
    },
    {
      name: 'Campo',
      fields: [
        { key: 'responsavel_tecnico', label: 'Responsável técnico', type: 'text' },
        { key: 'crea_rt', label: 'CREA do RT', type: 'text' },
        { key: 'data_saida_campo', label: 'Data saída a campo', type: 'date' },
        { key: 'data_retorno_campo', label: 'Data retorno', type: 'date' },
        { key: 'art_numero', label: 'Nº ART', type: 'text' },
        { key: 'art_valor', label: 'Valor ART (R$)', type: 'number' },
        { key: 'coordenadas_levantamento', label: 'Coordenadas do levantamento', type: 'text' },
        { key: 'acompanhante_contratante', label: 'Acompanhante do contratante', type: 'text' },
        { key: 'observacoes_campo', label: 'Observações de campo', type: 'textarea' },
        { key: 'art_quitada', label: 'ART quitada', type: 'boolean' },
      ],
    },
    {
      name: 'SIGEF',
      fields: [
        { key: 'codigo_sigef', label: 'Código SIGEF', type: 'text' },
        { key: 'data_certificacao_sigef', label: 'Data certificação', type: 'date' },
        { key: 'area_certificada_ha', label: 'Área certificada (ha)', type: 'number' },
        { key: 'perimetro_m', label: 'Perímetro (m)', type: 'number' },
        { key: 'data_envio_sigef', label: 'Data envio SIGEF', type: 'date' },
        { key: 'sistema_geodesico', label: 'Sistema geodésico', type: 'text' },
        { key: 'observacoes_sigef', label: 'Observações SIGEF', type: 'textarea' },
        { key: 'memorial_gerado', label: 'Memorial descritivo gerado', type: 'boolean' },
        { key: 'planta_gerada', label: 'Planta/mapa gerado', type: 'boolean' },
        { key: 'sobreposicao_detectada', label: '⚠ Sobreposição detectada (bloqueia avanço)', type: 'boolean' },
      ],
    },
    {
      name: 'Montagem da pasta',
      fields: [
        { key: 'requerimento_assinado', label: 'Requerimento de averbação', type: 'boolean' },
        { key: 'firmas_reconhecidas', label: 'Firmas reconhecidas', type: 'boolean' },
        { key: 'declaracao_limites_assinada', label: 'Declaração de respeito de limites', type: 'boolean' },
        { key: 'declaracao_localizacao', label: 'Declaração VTI/VTN', type: 'boolean' },
        { key: 'pasta_completa', label: 'Pasta completa', type: 'boolean' },
        { key: 'data_montagem_inicio', label: 'Início montagem', type: 'date' },
        { key: 'data_montagem_conclusao', label: 'Conclusão montagem', type: 'date' },
        { key: 'pendencias_pasta', label: 'Pendências da pasta', type: 'textarea' },
      ],
    },
    {
      name: 'Cartório',
      fields: [
        { key: 'numero_protocolo', label: 'Nº protocolo', type: 'text' },
        { key: 'data_protocolo', label: 'Data protocolo', type: 'date' },
        { key: 'data_previsao_averbacao', label: 'Previsão averbação', type: 'date' },
        { key: 'valor_emolumentos', label: 'Valor emolumentos (R$)', type: 'number' },
        { key: 'valor_pago_cartorio', label: 'Valor pago (R$)', type: 'number' },
        { key: 'data_exigencia', label: 'Data exigência', type: 'date' },
        { key: 'exigencias_cartorio', label: 'Exigências do cartório', type: 'textarea' },
        { key: 'observacoes_cartorio', label: 'Observações do cartório', type: 'textarea' },
      ],
    },
    {
      name: 'Concluído',
      fields: [
        { key: 'numero_averbacao', label: 'Nº averbação', type: 'text' },
        { key: 'data_averbacao', label: 'Data averbação', type: 'date' },
        { key: 'area_averbada_ha', label: 'Área averbada (ha)', type: 'number' },
        { key: 'valor_total_servico', label: 'Valor total serviço (R$)', type: 'number' },
        { key: 'data_entrega_cliente', label: 'Data entrega', type: 'date' },
        { key: 'forma_entrega', label: 'Forma de entrega', type: 'select', options: FORMA_ENTREGA },
        { key: 'observacoes_conclusao', label: 'Observações finais', type: 'textarea' },
        { key: 'documentos_entregues_cliente', label: 'Documentos entregues ao cliente', type: 'boolean' },
        { key: 'cliente_notificado', label: 'Cliente notificado', type: 'boolean' },
        { key: 'nota_fiscal_emitida', label: 'Nota fiscal emitida', type: 'boolean' },
      ],
    },
  ],
};

// ── Pipe CCIR — colunas 10-12 ──
const PIPE_CCIR: PipeSpec = {
  colStart: 10,
  phases: [
    {
      name: 'Documentação CCIR',
      // Antes tinha um select 'Tipo de alteração' (novo/titularidade/area/outro)
      // mas a coluna 'tipo' não existe — backend pulava. Removido.
      fields: [],
    },
    {
      name: 'Execução INCRA',
      fields: [
        { key: 'data_averbacao', label: 'Data execução', type: 'date' },
        { key: 'numero_protocolo', label: 'Protocolo INCRA', type: 'text' },
        { key: 'data_protocolo', label: 'Data envio INCRA', type: 'date' },
        { key: 'data_previsao_conclusao', label: 'Previsão retorno', type: 'date' },
        { key: 'status', label: 'Status INCRA', type: 'select', options: CCIR_STATUS_INCRA },
      ],
    },
    {
      name: 'Emissão e entrega',
      fields: [
        { key: 'data_certificacao_sigef', label: 'Data CCIR emitido', type: 'date' },
        { key: 'data_entrega_cliente', label: 'Data entrega', type: 'date' },
        { key: 'forma_entrega', label: 'Forma de entrega', type: 'select', options: FORMA_ENTREGA },
        { key: 'cliente_notificado', label: 'Cliente notificado', type: 'boolean' },
      ],
    },
  ],
};

// ── Pipe Titulação — colunas 20-26 ──
const PIPE_TITULACAO: PipeSpec = {
  colStart: 20,
  phases: [
    {
      name: 'Entrada',
      fields: [
        { key: 'municipio', label: 'Município', type: 'text' },
        { key: 'area_estimada_ha', label: 'Área estimada (ha)', type: 'number' },
        { key: 'tipo_propriedade', label: 'Tipo de propriedade', type: 'select', options: TIPO_PROPRIEDADE },
        { key: 'data_entrada', label: 'Data de entrada', type: 'date' },
        { key: 'requerente_cpf', label: 'CPF do requerente', type: 'text' },
        // Subtipo de titulação foi unificado com tipo_titulo (fase 7 - Concluído).
        // Aqui não persistia (coluna 'tipo' não existe).
        { key: 'tipo_vinculo_dominial', label: 'Vínculo dominial', type: 'select', options: VINCULO_DOMINIAL },
        { key: 'descricao_vinculo', label: 'Descrição do vínculo', type: 'text', showIf: isPosse },
        { key: 'situacao_cadeia_dominial', label: 'Situação da cadeia dominial', type: 'select', options: TIT_CADEIA_DOM },
        { key: 'viavel', label: 'Viável?', type: 'select', options: VIAVEL },
        { key: 'impedimentos_detectados', label: 'Impedimentos detectados', type: 'textarea' },
        { key: 'observacoes_entrada', label: 'Observações de entrada', type: 'textarea' },
        { key: 'cadeia_nominal_desde_2008', label: 'Cadeia nominal desde 2008', type: 'boolean', showIf: isPosse },
        { key: 'sobreposicao_snci', label: '⚠ Sobreposição detectada no SNCI/INCRA', type: 'boolean' },
      ],
    },
    {
      name: 'Documentação',
      fields: [
        { key: 'documento_originario_tipo', label: 'Tipo do documento originário', type: 'select', options: TIT_DOC_ORIGINARIO },
        { key: 'doc_matricula', label: 'Certidão de inteiro teor / matrícula', type: 'boolean' },
        { key: 'doc_ccir', label: 'CCIR quitado', type: 'boolean' },
        { key: 'doc_itr', label: 'ITR quitado', type: 'boolean' },
        { key: 'doc_car', label: 'CAR ativo', type: 'boolean' },
        { key: 'doc_rg_cpf', label: 'RG e CPF do requerente', type: 'boolean' },
        { key: 'doc_certidao_casamento', label: 'Certidão de casamento', type: 'boolean' },
        { key: 'doc_geo_certificado', label: 'GEO certificado no SIGEF', type: 'boolean' },
        { key: 'doc_documento_originario', label: 'Documento originário', type: 'boolean' },
        { key: 'possui_procurador', label: 'Representado por procurador', type: 'boolean' },
        { key: 'nome_procurador', label: 'Nome do procurador', type: 'text', showIf: (c) => Boolean(c.possui_procurador) },
        { key: 'cpf_procurador', label: 'CPF do procurador', type: 'text', showIf: (c) => Boolean(c.possui_procurador) },
        { key: 'doc_procuracao', label: 'Procuração pública entregue', type: 'boolean', showIf: (c) => Boolean(c.possui_procurador) },
        { key: 'regime_casamento', label: 'Regime de casamento', type: 'select', options: REGIME_CASAMENTO },
        { key: 'data_documentacao_completa', label: 'Data documentação completa', type: 'date' },
        { key: 'pendencias_documentacao', label: 'Pendências', type: 'textarea' },
      ],
    },
    {
      name: 'Protocolo PGT',
      fields: [
        { key: 'numero_requerimento', label: 'Nº requerimento', type: 'text' },
        { key: 'numero_processo_incra', label: 'Nº processo INCRA', type: 'text' },
        { key: 'data_montagem_inicio', label: 'Início montagem', type: 'date' },
        { key: 'data_montagem_conclusao', label: 'Conclusão montagem', type: 'date' },
        { key: 'data_protocolo', label: 'Data protocolo INCRA', type: 'date' },
        { key: 'pendencias_pasta', label: 'Pendências da pasta', type: 'textarea' },
        { key: 'requerimento_assinado', label: 'Requerimento assinado', type: 'boolean' },
        { key: 'firmas_reconhecidas', label: 'Firmas reconhecidas', type: 'boolean' },
        { key: 'declaracao_limites_assinada', label: 'Declaração de limites assinada', type: 'boolean' },
        { key: 'declaracao_localizacao', label: 'Declaração de localização', type: 'boolean' },
        { key: 'pasta_completa', label: 'Pasta completa', type: 'boolean' },
      ],
    },
    {
      name: 'Aguardando análise INCRA',
      fields: [
        { key: 'data_ultima_consulta_incra', label: 'Última consulta INCRA', type: 'date' },
        { key: 'numero_exigencias_incra', label: 'Nº exigências (INCRA)', type: 'number' },
        { key: 'data_exigencia', label: 'Data das exigências', type: 'date' },
        { key: 'data_atendimento_exigencias', label: 'Data atendimento exigências', type: 'date' },
        { key: 'exigencias_cartorio', label: 'Exigências do cartório/INCRA', type: 'textarea' },
        { key: 'pendencias', label: 'Pendências', type: 'textarea' },
        { key: 'vistoria_necessaria', label: 'Vistoria INCRA necessária', type: 'boolean' },
      ],
    },
    {
      name: 'Vistoria / CRO',
      fields: [
        { key: 'data_agendamento_campo', label: 'Agendamento campo', type: 'date' },
        { key: 'data_campo_realizado', label: 'Campo realizado', type: 'date' },
        { key: 'data_saida_campo', label: 'Saída a campo', type: 'date' },
        { key: 'data_retorno_campo', label: 'Retorno do campo', type: 'date' },
        { key: 'data_vistoria_incra', label: 'Vistoria INCRA', type: 'date' },
        { key: 'resultado_vistoria', label: 'Resultado da vistoria', type: 'select', options: TIT_RESULT_VIST },
        { key: 'numero_cro', label: 'Nº CRO (protocolo)', type: 'text' },
        { key: 'numero_cro_definitivo', label: 'Nº CRO definitivo', type: 'text' },
        { key: 'data_emissao_cro', label: 'Emissão CRO', type: 'date' },
        { key: 'coordenadas_levantamento', label: 'Coordenadas levantamento', type: 'text' },
        { key: 'acompanhante_contratante', label: 'Acompanhante do contratante', type: 'text' },
        { key: 'observacoes_campo', label: 'Observações de campo', type: 'textarea' },
        { key: 'dados_enviados_escritorio', label: 'Dados enviados ao escritório', type: 'boolean' },
      ],
    },
    {
      name: 'Cartório',
      fields: [
        { key: 'data_registro_cartorio', label: 'Data registro cartório', type: 'date' },
        { key: 'data_previsao_averbacao', label: 'Previsão averbação', type: 'date' },
        { key: 'data_averbacao', label: 'Data averbação', type: 'date' },
        { key: 'numero_averbacao', label: 'Nº averbação', type: 'text' },
        { key: 'area_averbada_ha', label: 'Área averbada (ha)', type: 'number' },
        { key: 'valor_emolumentos_cartorio', label: 'Emolumentos cartório (R$)', type: 'number' },
        { key: 'valor_emolumentos', label: 'Emolumentos (geral, R$)', type: 'number' },
        { key: 'valor_pago_cartorio', label: 'Valor pago (R$)', type: 'number' },
        { key: 'numero_certidao_inteiro_teor', label: 'CIT nº', type: 'text' },
        { key: 'data_certidao_inteiro_teor', label: 'CIT emissão', type: 'date' },
        { key: 'validade_certidao_inteiro_teor', label: 'CIT validade', type: 'date' },
        { key: 'observacoes_cartorio', label: 'Observações do cartório', type: 'textarea' },
      ],
    },
    {
      name: 'Concluído',
      fields: [
        { key: 'tipo_titulo', label: 'Tipo do título', type: 'select', options: TIT_TIPO_TITULO },
        { key: 'numero_titulo', label: 'Nº título', type: 'text' },
        { key: 'data_emissao_titulo', label: 'Emissão do título', type: 'date' },
        { key: 'matricula_pos_titulacao', label: 'Matrícula pós-titulação', type: 'text' },
        { key: 'valor_total_servico', label: 'Valor total serviço (R$)', type: 'number' },
        { key: 'data_entrega_cliente', label: 'Data entrega cliente', type: 'date' },
        { key: 'forma_entrega', label: 'Forma de entrega', type: 'select', options: FORMA_ENTREGA },
        { key: 'observacoes_conclusao', label: 'Observações finais', type: 'textarea' },
        { key: 'observacoes_finais', label: 'Observações finais (cliente)', type: 'textarea' },
        { key: 'documentos_entregues_cliente', label: 'Documentos entregues ao cliente', type: 'boolean' },
        { key: 'cliente_notificado', label: 'Cliente notificado', type: 'boolean' },
        { key: 'nota_fiscal_emitida', label: 'Nota fiscal emitida', type: 'boolean' },
      ],
    },
  ],
};

function pickPipe(c: Record<string, unknown>): PipeSpec | null {
  const tipo = String(c.tipo_processo ?? '').toLowerCase();
  if (tipo === 'triagem_geo') return PIPE_TRIAGEM_GEO;
  if (tipo === 'ccir') return PIPE_CCIR;
  if (tipo === 'titulacao') return PIPE_TITULACAO;
  // georreferenciamento (default) — também cobre col 1-7 sem tipo definido
  const col = Number(c.kanban_coluna);
  if (Number.isFinite(col)) {
    if (col >= 20) return PIPE_TITULACAO;
    if (col >= 10) return PIPE_CCIR;
  }
  return PIPE_GEO;
}

function asStr(v: unknown): string | null {
  return v != null && v !== '' ? String(v) : null;
}
function asRaw(v: unknown): string | number | boolean | string[] | null {
  if (v == null || v === '') return null;
  if (Array.isArray(v)) return v.map(String);
  if (typeof v === 'number' || typeof v === 'boolean') return v;
  return String(v);
}
function selectedStrings(v: unknown): string[] {
  if (Array.isArray(v)) return v.map(String);
  if (typeof v !== 'string' || !v.trim()) return [];
  try {
    const parsed: unknown = JSON.parse(v);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}
function fmtDate(v: unknown): string | null {
  return v ? formatDateBR(String(v)) : null;
}
function fmtNum(v: unknown, suffix = ''): string | null {
  return v != null && v !== '' ? `${Number(v).toLocaleString('pt-BR')}${suffix}` : null;
}
function fmtBRL(v: unknown): string | null {
  return v != null && v !== ''
    ? Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : null;
}

function displayFor(f: PhaseField, value: unknown): string {
  if (f.type === 'date') return fmtDate(value) ?? '—';
  if (f.type === 'select' && f.options)
    return labelFromOptions(value, f.options) ?? '—';
  if (f.type === 'boolean') {
    if (value === true || value === 'true' || value === 't') return 'Sim';
    if (value === false || value === 'false' || value === 'f') return 'Não';
    return '—';
  }
  if (f.type === 'multiselect') {
    const selected = selectedStrings(value);
    if (!selected.length) return '—';
    return selected.map((item) => labelFromOptions(item, f.options ?? []) ?? item).join('; ');
  }
  if (f.type === 'number') {
    if (/valor|honorario|custas|emolumentos/i.test(f.key)) return fmtBRL(value) ?? '—';
    if (/area/i.test(f.key)) return fmtNum(value, ' ha') ?? '—';
    if (/perimetro/i.test(f.key)) return fmtNum(value, ' m') ?? '—';
    return fmtNum(value) ?? '—';
  }
  return asStr(value) ?? '—';
}

export const fundiarioDetailFieldMap: DetailFieldMap = (card) => {
  const out: ReturnType<DetailFieldMap> = [];
  const c = card as Record<string, unknown>;
  const colNum = Number(c.kanban_coluna) || 0;

  // ── Dados principais (sempre no topo, read-only) ──
  const responsaveisList = parseResponsaveis(c.responsaveis);
  out.push({
    section: 'Dados principais',
    label: 'CPF',
    value: asStr(c.cliente_cpf) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Imóvel',
    value: asStr(c.imovel_denominacao) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Matrícula',
    value: asStr(c.imovel_matricula) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Cartório',
    value: asStr(c.cartorio) ?? '—',
    alwaysShow: true,
  });
  out.push({
    section: 'Dados principais',
    label: 'Responsável(is)',
    value: '',
    alwaysShow: true,
    type: 'responsaveis',
    responsaveis: responsaveisList,
  });
  out.push({
    section: 'Dados principais',
    label: 'Previsão de conclusão',
    value: fmtDate(c.data_previsao_conclusao) ?? '—',
    alwaysShow: true,
    field: 'data_previsao_conclusao',
    type: 'date',
    rawValue: asRaw(c.data_previsao_conclusao),
  });
  if (c.tipo_processo === 'triagem_geo' && c.card_filho_id) {
    out.push({
      section: 'Dados principais',
      label: 'CARD filho GEO',
      value: `#${String(c.card_filho_id)}`,
      alwaysShow: true,
    });
  }

  // ── Fases por pipe ──
  const pipe = pickPipe(c);
  if (pipe && colNum >= pipe.colStart) {
    const explicitIndex = pipe.columnIds?.indexOf(colNum) ?? -1;
    const phaseIndex = explicitIndex >= 0 ? explicitIndex + 1 : colNum - pipe.colStart + 1; // 1-based
    const visibleCount = Math.min(phaseIndex, pipe.phases.length);
    for (let i = 1; i <= visibleCount; i++) {
      const phase = pipe.phases[i - 1];
      if (!phase) continue;
      const sectionName = `${i}. ${phase.name}`;
      for (const f of phase.fields) {
        if (f.showIf && !f.showIf(c)) continue;
        const dynamicOptions = f.key === 'imoveis_contrato'
          ? [
              ...(Array.isArray(c.cliente_imoveis) ? c.cliente_imoveis : []).map((raw) => {
                const imovel = raw as Record<string, unknown>;
                const denominacao = String(imovel.denominacao ?? '').trim();
                const matricula = String(imovel.matricula ?? '').trim();
                const local = [imovel.municipio, imovel.uf].filter(Boolean).map(String).join('/');
                return {
                  value: String(imovel.id),
                  label: [denominacao || 'Imóvel', matricula ? `Matrícula ${matricula}` : '', local]
                    .filter(Boolean)
                    .join(' — '),
                };
              }),
              { value: OUTRO_IMOVEL_VALUE, label: 'Outro imóvel (não cadastrado)' },
            ]
          : f.options;
        const fieldWithOptions = dynamicOptions ? { ...f, options: dynamicOptions } : f;
        const contractPropertyField = ['imoveis_contrato', 'imoveis_contrato_outros'].includes(f.key);
        const editable = i === phaseIndex || (contractPropertyField && phaseIndex === 4);
        const item: ReturnType<DetailFieldMap>[number] = {
          section: contractPropertyField && phaseIndex === 4
            ? '4. Emissão do Contrato'
            : sectionName,
          label: f.label,
          value: displayFor(fieldWithOptions, c[f.key]),
          alwaysShow: editable || f.type === 'emitir_proposta_fundiaria',
          rawValue: asRaw(c[f.key]),
        };
        if (editable && f.type !== 'emitir_proposta_fundiaria') {
          item.field = f.key;
          item.type = f.type;
          if (dynamicOptions) item.options = dynamicOptions;
        } else if (f.type === 'emitir_proposta_fundiaria') {
          item.type = f.type;
        }
        out.push(item);
      }
    }
  }

  // ── Observações gerais — seção própria, sem o agrupador "Outros" ──
  out.push({
    section: 'Observações gerais',
    label: 'Observações',
    value: asStr(c.observacoes) ?? '',
    alwaysShow: true,
    field: 'observacoes',
    type: 'textarea',
    rawValue: asRaw(c.observacoes),
  });

  return out;
};
